import {
  MENU_ITEMS,
  HORIZONTAL_MENU_ITEMS,
  TWO_COl_MENU_ITEMS,
  SCORECARD_MENU_ITEM,
  REFERRAL_MENU_ITEMS,
  BECOME_SERVICE_PROVIDER_MENU_ITEM,
  MenuItemTypes,
} from "../constants/menu";
import { isSupplierLive, KYC_MENU_KEYS } from "./supplierLive";
import { getReferrerFlags, REFERRER_MENU_KEYS, REFERRER_ONBOARDING_MENU_KEYS, upgradeInProgress } from "./supplierReferrer";

/** Drop section titles left with no items under them after filtering. */
const dropEmptyTitles = (items: MenuItemTypes[]): MenuItemTypes[] =>
  items.filter((m, i) => !m.isTitle || (items[i + 1] !== undefined && !items[i + 1].isTitle));

/**
 * The Referrals section sits second in the sidebar — its own titled block
 * straight below Home, not buried after Finance (owner, 2026-10-15). Anchored
 * on the Home section's Dashboard entry, so adding Home items keeps it second.
 */
const withReferralItems = (items: MenuItemTypes[]): MenuItemTypes[] => {
  const out = [...items];
  const dashboardIdx = out.findIndex((i) => !i.isTitle && i.key === "dashboard");
  out.splice(dashboardIdx >= 0 ? dashboardIdx + 1 : out.length, 0, ...REFERRAL_MENU_ITEMS);
  return out;
};

const getMenuItems = (options?: { scorecardEnabled?: boolean }) => {
  const { isReferrerOnly, canActAsReferrer, serviceProviderUpgrade } = getReferrerFlags();
  const live = isSupplierLive();

  // Referrer-only companies (affiliates) have no inventory: their own short menu
  // (owner decision Q3), plus onboarding until Verati takes them live — and again
  // while they work through the optional service-provider upgrade, which asks the
  // rest of the onboarding. "Become a service provider" is offered until it starts.
  // Until it is live an affiliate sees only help and onboarding: nothing else works yet.
  if (isReferrerOnly) {
    const upgrading = upgradeInProgress(serviceProviderUpgrade);
    const allowed = live ? REFERRER_MENU_KEYS : REFERRER_ONBOARDING_MENU_KEYS;
    const items = dropEmptyTitles(
      withReferralItems(MENU_ITEMS).filter(
        (m) => m.isTitle || allowed.includes(m.key) || ((!live || upgrading) && KYC_MENU_KEYS.includes(m.key))
      )
    );
    if (serviceProviderUpgrade !== "APPROVED") items.push(BECOME_SERVICE_PROVIDER_MENU_ITEM);
    return items;
  }

  // Until the supplier is live, restrict the sidebar to the KYC/onboarding items.
  if (!live) {
    return MENU_ITEMS.filter((m) => KYC_MENU_KEYS.includes(m.key));
  }
  const base = canActAsReferrer ? withReferralItems(MENU_ITEMS) : MENU_ITEMS;
  if (!options?.scorecardEnabled) return base;
  // AI Request Dispatcher (P-4): inject the flag-gated "My Scorecard" entry
  // after Offers only when the my-scorecard probe returned 200.
  const items = [...base];
  const offersIdx = items.findIndex((item) => item.key === "offers");
  items.splice(offersIdx >= 0 ? offersIdx + 1 : items.length, 0, SCORECARD_MENU_ITEM);
  return items;
};

const getHorizontalMenuItems = () => {
  // NOTE - You can fetch from server and return here as well
  return HORIZONTAL_MENU_ITEMS;
};

const getTwoColumnMenuItems = () => {
  // NOTE - You can fetch from server and return here as well
  return TWO_COl_MENU_ITEMS;
};

const findAllParent = (
  menuItems: MenuItemTypes[],
  menuItem: MenuItemTypes
): string[] => {
  let parents: string[] = [];
  const parent = findMenuItem(menuItems, menuItem["parentKey"]);

  if (parent) {
    parents.push(parent["key"]);
    if (parent["parentKey"])
      parents = [...parents, ...findAllParent(menuItems, parent)];
  }

  return parents;
};

const findMenuItem = (
  menuItems: MenuItemTypes[] | undefined,
  menuItemKey: MenuItemTypes["key"] | undefined
): MenuItemTypes | null => {
  if (menuItems && menuItemKey) {
    for (var i = 0; i < menuItems.length; i++) {
      if (menuItems[i].key === menuItemKey) {
        return menuItems[i];
      }
      var found = findMenuItem(menuItems[i].children, menuItemKey);
      if (found) return found;
    }
  }
  return null;
};

export {
  getMenuItems,
  getHorizontalMenuItems,
  getTwoColumnMenuItems,
  findAllParent,
  findMenuItem,
};
